import axios from 'axios';
import { APP_CONFIG } from '../utils/constants';

const BASE_URL = APP_CONFIG.API_BASE_URL;
// const BASE_URL_DEMO = APP_CONFIG.API_BASE_URL_DEMO; // Unused, kept for reference

// Create axios instance for certificates
const certificatesAPI = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: APP_CONFIG.REQUEST_TIMEOUT,
});

// Add token to requests automatically
certificatesAPI.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for better error handling
certificatesAPI.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired or unauthorized, redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const certificatesService = {
  getCertificates: async (params = {}) => {
    try {
      const {
        page = 1,
        pageSize = 10,
        searchTerm = '',
        sortBy = 'createdAt',
        sortOrder = 'desc',
        status = '',
        dateFrom = '',
        dateTo = '',
      } = params;

      const queryParams = {
        page,
        pageSize,
        searchTerm,
        sortBy,
        sortOrder,
        ...(status && { status }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(params.isDraft !== undefined && { isDraft: params.isDraft }),
        ...(params.isReCertificate !== undefined && { isReCertificate: params.isReCertificate }),
      };

      const response = await certificatesAPI.get('/api/Certificates', {
        params: queryParams,
      });

      return {
        data: response.data || [],
        totalCount:
          response.headers['x-total-count'] || response.data?.length || 0,
        totalPages: Math.ceil(
          (response.headers['x-total-count'] || response.data?.length || 0) /
            pageSize
        ),
        currentPage: page,
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get certificate by ID with detailed information
  getCertificateById: async id => {
    try {
      const response = await certificatesAPI.get(`/api/Certificates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get certificate by COC number
  getCertificateByCocNumber: async cocNumber => {
    try {
      const response = await certificatesAPI.get(
        `/api/Certificates/by-coc-number/${cocNumber}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new certificate (Office role only) - Now creates DRAFT by default
  createCertificate: async certificateData => {
    try {
      const response = await certificatesAPI.post(
        '/api/Certificates',
        certificateData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update certificate (Office role only)
  updateCertificate: async (id, certificateData) => {
    try {
      const response = await certificatesAPI.put(
        `/api/Certificates/${id}`,
        certificateData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete certificate
  deleteCertificate: async id => {
    try {
      const response = await certificatesAPI.delete(`/api/Certificates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete attachment
  deleteAttachment: async attachmentId => {
    try {
      const response = await certificatesAPI.delete(
        `/api/Certificates/delete-attachment/${attachmentId}`,
        {
          headers: {
            accept: 'text/plain',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Sync COC file by COC number
  syncCOCFile: async cocNumber => {
    try {
      const response = await certificatesAPI.post(
        `/api/Certificates/SyncCOCFile?cocNumber=${cocNumber}`,
        '',
        {
          headers: {
            accept: '*/*',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== NEW: DRAFT TO PRODUCTION CONVERSION ====================

  // Change draft status to production (requires issuance and expiry dates)
  changeDraftStatus: async (id, draftStatusData) => {
    try {
      const { issuanceDate, expiryDate } = draftStatusData;

      const isoIssuanceDate = new Date(issuanceDate).toISOString();
      const isoExpiryDate = new Date(expiryDate).toISOString();

      const response = await certificatesAPI.post(
        `/api/Certificates/change-draft-status/${id}`,
        {
          IssuanceDate: isoIssuanceDate,
          ExpiryDate: isoExpiryDate,
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== UPDATED: PDF HANDLING WITH NEW STRUCTURE ====================

  // Get certificate PDF URLs (returns both demo and final URLs)
  getCertificatePDFUrls: async id => {
    try {
      const response = await certificatesAPI.get(
        `/api/Certificates/${id}/pdf-url`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get certificate PDF for viewing (handles demo vs final automatically)
  getCertificatePDFForViewing: async (id, isDraft = false) => {
    try {
      // First get the PDF URLs structure
      const pdfUrlsResponse = await certificatesAPI.get(
        `/api/Certificates/${id}/pdf-url`
      );

      const pdfData = pdfUrlsResponse.data;

      if (!pdfData.success) {
        throw new Error('Failed to get PDF URLs');
      }

      // Determine which PDF URL to use
      let pdfUrl;
      if (isDraft && pdfData.demo?.exists) {
        pdfUrl = pdfData.demo.pdfUrl;
      } else if (!isDraft && pdfData.final?.exists) {
        pdfUrl = pdfData.final.pdfUrl;
      } else if (pdfData.demo?.exists) {
        // Fallback to demo if final doesn't exist
        pdfUrl = pdfData.demo.pdfUrl;
      } else {
        throw new Error('No PDF available for viewing');
      }

      // Return the PDF URL for embedding in viewer
      return {
        pdfUrl,
        fileName: isDraft ? pdfData.demo?.fileName : pdfData.final?.fileName,
        isDemoVersion: isDraft || !pdfData.final?.exists,
        pdfData, // Return full structure for additional info
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Download certificate PDF (handles demo vs final automatically)
  downloadCertificatePDF: async (id, isDraft = false) => {
    try {
      // First get the PDF URLs structure
      const pdfUrlsResponse = await certificatesAPI.get(
        `/api/Certificates/${id}/pdf-url`
      );

      const pdfData = pdfUrlsResponse.data;

      if (!pdfData.success) {
        throw new Error('Failed to get PDF URLs');
      }

      // Determine which PDF to download
      let pdfUrl;
      if (isDraft && pdfData.demo?.exists) {
        pdfUrl = pdfData.demo.pdfUrl;
      } else if (!isDraft && pdfData.final?.exists) {
        pdfUrl = pdfData.final.pdfUrl;
      } else if (pdfData.demo?.exists) {
        // Fallback to demo if final doesn't exist
        pdfUrl = pdfData.demo.pdfUrl;
      } else {
        throw new Error('No PDF available for download');
      }

      // Download the PDF from the URL
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.statusText}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== LEGACY PDF METHODS (for backward compatibility) ====================

  // Get certificate PDF (legacy method - now uses new structure)
  getCertificatePDF: async (id, isDemo = false) => {
    try {
      const response = await certificatesAPI.get(
        `/api/Certificates/${id}/pdf`,
        {
          params: { isDemo },
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get certificate PDF URL (legacy method)
  getCertificatePDFUrl: async id => {
    try {
      const response = await certificatesAPI.get(
        `/api/Certificates/${id}/pdf-url`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== VALIDATION AND UTILITY METHODS ====================

  // Check if certificate is ready for production conversion
  validateDraftConversion: async id => {
    try {
      const certificate = await certificatesService.getCertificateById(id);

      const errors = [];

      // Check required fields for production
      if (!certificate.ImporterName) errors.push('Importer name is required');
      if (!certificate.ExporterName) errors.push('Exporter name is required');
      if (!certificate.InvoiceNumber) errors.push('Invoice number is required');

      return {
        isValid: errors.length === 0,
        errors,
        certificate,
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Generate suggested issuance and expiry dates
  generateSuggestedDates: () => {
    const now = new Date();

    const issuanceDate = new Date(now);
    const expiryDate = new Date(now);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1); // 1 year validity

    const formatDate = date => {
      // Formats to: "YYYY-MM-DDTHH:mm:ss" (no milliseconds, no Z)
      return date.toISOString().split('.')[0];
    };

    return {
      issuanceDate: formatDate(issuanceDate),
      expiryDate: formatDate(expiryDate),
    };
  },

  // ==================== OTHER EXISTING METHODS ====================

  // Get next COC number
  getNextCocNumber: async (prefix = 'BC') => {
    try {
      const response = await certificatesAPI.get(
        '/api/Certificates/next-coc-number',
        {
          params: { prefix },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Check COC number availability
  checkCocNumber: async cocNumber => {
    try {
      const response = await certificatesAPI.get(
        `/api/Certificates/check-coc/${cocNumber}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get certificates statistics (updated to handle draft vs production)
  // NOTE: This method loads a limited set of certificates for statistics calculation.
  // For better performance, consider using a dedicated backend statistics API endpoint.
  getCertificatesStats: async () => {
    try {
      // Load a reasonable amount of recent certificates (500 max)
      // WARNING: This is client-side filtering and may not reflect accurate totals for large datasets
      const response = await certificatesAPI.get('/api/Certificates', {
        params: { page: 1, pageSize: 500 },
      });

      const certificates = response.data || [];
      const totalCount = parseInt(response.headers['x-total-count']) || certificates.length;
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const thisYear = new Date(now.getFullYear(), 0, 1);

      return {
        total: totalCount, // Use header total count for accurate total
        drafts: certificates.filter(cert => cert.IsDraft).length,
        production: certificates.filter(cert => !cert.IsDraft).length,
        thisMonth: certificates.filter(
          cert => new Date(cert.CreatedAt || cert.IssuanceDate) >= thisMonth
        ).length,
        lastMonth: certificates.filter(cert => {
          const certDate = new Date(cert.CreatedAt || cert.IssuanceDate);
          return certDate >= lastMonth && certDate < thisMonth;
        }).length,
        thisYear: certificates.filter(
          cert => new Date(cert.CreatedAt || cert.IssuanceDate) >= thisYear
        ).length,
        pending: certificates.filter(
          cert =>
            cert.Status === 'pending' ||
            cert.Status === 'review' ||
            !cert.Status
        ).length,
        approved: certificates.filter(
          cert => cert.Status === 'approved' || cert.Status === 'active'
        ).length,
        expired: certificates.filter(
          cert => cert.ExpiryDate && new Date(cert.ExpiryDate) < now
        ).length,
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== SYNC ENDPOINTS ====================

  // Get unsynced certificates (Admin only)
  getUnSyncCertificates: async (params = {}) => {
    try {
      const {
        page = 1,
        pageSize = 10,
        searchTerm = '',
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = params;

      const queryParams = {
        page,
        pageSize,
        searchTerm,
        sortBy,
        sortOrder,
        ...(params.isReCertificate !== undefined && { isReCertificate: params.isReCertificate }),
      };

      const response = await certificatesAPI.get(
        '/api/Certificates/GetUnSyncCertificates',
        {
          params: queryParams,
        }
      );

      return {
        data: response.data || [],
        totalCount:
          response.headers['x-total-count'] || response.data?.length || 0,
        totalPages: Math.ceil(
          (response.headers['x-total-count'] || response.data?.length || 0) /
            pageSize
        ),
        currentPage: page,
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Sync certificate to Stage 1 (Admin only)
  syncToStage1: async certificateId => {
    try {
      const response = await certificatesAPI.post(
        `/api/Certificates/SyncToStage1/${certificateId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Sync certificate to Stage 2 (Admin only)
  syncToStage2: async certificateId => {
    try {
      const response = await certificatesAPI.post(
        `/api/Certificates/SyncToStage2/${certificateId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Auto-sync all eligible certificates to Stage 2 (Admin only)
  autoSyncToStage2: async () => {
    try {
      const response = await certificatesAPI.post(
        '/api/Certificates/AutoSyncToStage2'
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get sync statistics
  // NOTE: Loads limited certificates for sync stats calculation
  // Consider using a dedicated backend statistics API endpoint for better performance
  getSyncStats: async () => {
    try {
      // Load a reasonable amount for statistics (200 max)
      const response = await certificatesAPI.get(
        '/api/Certificates/GetUnSyncCertificates',
        {
          params: { page: 1, pageSize: 200 },
        }
      );

      const certificates = response.data || [];
      const totalCount = parseInt(response.headers['x-total-count']) || certificates.length;

      return {
        totalUnsynced: totalCount, // Use header total count for accurate total
        stage1Pending: certificates.filter(cert => !cert.IsStage1Sync).length,
        stage2Pending: certificates.filter(
          cert => cert.IsStage1Sync && !cert.IsStage2Sync
        ).length,
        readyForStage2: certificates.filter(
          cert => cert.IsStage1Sync && !cert.IsStage2Sync
        ).length,
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== ATTACHMENT ENDPOINTS ====================

  // Add attachment to certificate
  addAttachment: async (certificateId, attachmentData) => {
    try {
      const formData = new FormData();
      formData.append('FileName', attachmentData.fileName);
      formData.append('File', attachmentData.file);
      formData.append('Description', attachmentData.description);

      const response = await certificatesAPI.post(
        `/api/Certificates/add-attachment/${certificateId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // NEW: Upload attachments directly by COC number (uses fixed API key)
  uploadAttachmentsDirect: async (cocNumber, files, titles = []) => {
    try {
      const formData = new FormData();

      // Add files to FormData (each file as separate 'files' entry)
      files.forEach(file => {
        formData.append('files', file);
      });

      // Add titles to FormData (each title as separate 'titles' entry)
      titles.forEach(title => {
        formData.append('titles', title);
      });

      // Make request with fixed API key (no token needed)
      const response = await axios.post(
        `https://inspect-share.bciraq.com/api/COC/UploadAttachmentsDirect?certNo=${cocNumber}`,
        formData,
        {
          headers: {
            accept: '*/*',
            'X-API-Key': 'INSPa7d9e3f21b6c4f28b4c3f2a9d89f12e3',
            'Content-Type': 'multipart/form-data',
          },
          timeout: APP_CONFIG.REQUEST_TIMEOUT,
        }
      );

      return response.data;
    } catch (error) {
      // Simple error handling without token logic
      console.error('Upload attachments direct error:', error);
      throw error.response?.data || error.message;
    }
  },

  // Get all attachments for a certificate
  getCertificateAttachments: async certificateId => {
    try {
      const response = await certificatesAPI.get(
        `/api/Certificates/${certificateId}/attachments`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== EXPORTER INFORMATION ENDPOINTS ====================

  // Save exporter information
  saveExporterInformation: async exporterData => {
    try {
      const response = await certificatesAPI.post(
        '/api/Certificates/exporter-information',
        exporterData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Search exporter information
  searchExporterInformation: async (searchTerm = '') => {
    try {
      const response = await certificatesAPI.get(
        '/api/Certificates/exporter-information/search',
        {
          params: { searchTerm },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== CERTIFICATES WITHOUT ATTACHMENTS ====================

  // Get certificates without attachments
  getCertificatesWithoutAttachments: async (params = {}) => {
    try {
      const {
        page = 1,
        pageSize = 10,
        searchTerm = '',
        sortBy = 'createdAt',
        sortOrder = 'desc',
      } = params;

      const queryParams = {
        page,
        pageSize,
        searchTerm,
        sortBy,
        sortOrder,
      };

      const response = await certificatesAPI.get(
        '/api/Certificates/Certificates/WithoutAttachments',
        {
          params: queryParams,
        }
      );

      return {
        data: response.data || [],
        totalCount:
          response.headers['x-total-count'] || response.data?.length || 0,
        totalPages: Math.ceil(
          (response.headers['x-total-count'] || response.data?.length || 0) /
            pageSize
        ),
        currentPage: page,
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export certificates without attachments to Excel
  exportCertificatesWithoutAttachmentsToExcel: async (params = {}) => {
    try {
      const {
        searchTerm = '',
        status = '',
        dateFrom = '',
        dateTo = '',
      } = params;

      const queryParams = {
        ...(searchTerm && { searchTerm }),
        ...(status && { status }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
      };

      const response = await certificatesAPI.get(
        '/api/Certificates/Certificates/WithoutAttachments/Excel',
        {
          params: queryParams,
          responseType: 'blob', // Important for downloading files
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload Excel file for certificates without attachments
  uploadExcelAttachments: async (certificateId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await certificatesAPI.post(
        '/api/Certificates/Certificates/WithoutAttachments/Excel',
        formData,
        {
          params: { certificateId },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ==================== REISSUE CERTIFICATE ====================

  // Reissue a completed certificate
  reissueCertificate: async (certificateId) => {
    try {
      const response = await certificatesAPI.post(
        `/api/Certificates/${certificateId}/reissue`,
        {},
        {
          timeout: 120000, // 2 minutes timeout for PDF generation
        }
      );
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - The reissue process is taking longer than expected. Please check if the certificate was created.');
      }
      throw error.response?.data || error.message;
    }
  },
};
